import { jsx as _jsx } from "react/jsx-runtime";
import { bemClassBuilder } from '@vista-digital/bem-class-builder';
import { addErrorNotification, getEmailAvailability, saveEmailAvailability, validateEmailAvailability } from '@vista-digital/occ-domain';
import { observer } from 'mobx-react';
import { forwardRef, useMemo, useState } from 'react';
import { useFormValidator } from '../../../hooks/use-form-validator';
import { Form } from '../../../subcomponents/form/form/form';
import { withComponentWrapper } from '../../../utils/with-component-wrapper';
import { MemberEmailCheckerFormContextInstance } from './types/context';
import { defaultMemberEmailCheckerFormData } from './types/data';
import { defaultMemberEmailCheckerFormProps, memberEmailCheckerFormPropsValidator } from './types/props';
import { getNotificationDetailsForError } from './utils/get-notification-details-for-error';
const b = bemClassBuilder('v-member-email-checker-form');
const MemberEmailCheckerFormBase = (props, ref) => {
    const { displayText, events, config, formId, children } = props;
    const [formData, setFormData] = useState(defaultMemberEmailCheckerFormData);
    const { email } = formData;
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { validator, validationResult } = useFormValidator(config.validation);
    const handleSubmit = async () => {
        const emailAvailability = await validateEmailAvailability(email);
        if (emailAvailability)
            await saveEmailAvailability(email, emailAvailability);
    };
    const handleAfterSuccessfulSubmit = async () => {
        var _a;
        const emailAvailability = await getEmailAvailability();
        if (emailAvailability)
            (_a = events.onAfterSuccessfulSubmit) === null || _a === void 0 ? void 0 : _a.call(events, { isAvailable: emailAvailability.isAvailable });
    };
    const handleFailedToSubmit = error => {
        var _a;
        addErrorNotification(getNotificationDetailsForError(displayText, error), error);
        (_a = events.onAfterFailedSubmit) === null || _a === void 0 ? void 0 : _a.call(events, error);
    };
    const handleFormSubmittingStateChanged = (submitting) => {
        var _a;
        setIsSubmitting(submitting);
        (_a = events.onFormSubmittingStateChanged) === null || _a === void 0 ? void 0 : _a.call(events, submitting);
    };
    const context = useMemo(() => ({
        onChange: (fieldName, value) => setFormData(data => (Object.assign(Object.assign({}, data), { [fieldName]: value }))),
        formData,
        validationResult,
        validationConfig: config.validation,
        validator,
        disabled: isSubmitting,
        loading: false
    }), [config.validation, formData, validationResult, isSubmitting, validator]);
    return (_jsx(Form, Object.assign({}, props, { id: formId, className: b.block(), validator: () => validator.validateForm(formData), events: Object.assign(Object.assign({}, events), { onSubmit: handleSubmit, onAfterSuccessfulSubmit: handleAfterSuccessfulSubmit, onAfterFailedSubmit: handleFailedToSubmit, onFormSubmittingStateChanged: handleFormSubmittingStateChanged }), setFormData: setFormData, ref: ref, children: _jsx(MemberEmailCheckerFormContextInstance.Provider, { value: context, children: children }) })));
};
const MemberEmailCheckerForm = observer(forwardRef(MemberEmailCheckerFormBase));
MemberEmailCheckerForm.displayName = 'MemberEmailCheckerForm';
/**
 * The **Member Email Checker Form** component determines whether an entered email address is available. It can be used as the start of a Sign In/Sign Up flow.
 */
export const MemberEmailCheckerFormComponent = withComponentWrapper(MemberEmailCheckerForm, defaultMemberEmailCheckerFormProps, memberEmailCheckerFormPropsValidator);
//# sourceMappingURL=member-email-checker-form.js.map